import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calculator, Gauge, Linkedin, MessageCircle, TrendingDown } from "lucide-react";
import { motion, useScroll, useSpring } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { useSEO } from "@/components/SEO";
import { FALLBACK_BLOGS } from "@/lib/content";
import { fetchBlogPosts, fetchPostBySlug, stripHtml, useWP } from "@/lib/wp";
import { formatDate } from "@/lib/format";

const CALCULATORS = {
  savings: { to: "/savings-calculator", label: "Savings Calculator", icon: TrendingDown, blurb: "See exactly what switching lenders could save you — monthly and lifetime." },
  eligibility: { to: "/eligibility-calculator", label: "Eligibility Calculator", icon: Gauge, blurb: "Find out how much you can borrow from your income in one minute." },
  emi: { to: "/emi-calculator", label: "EMI Calculator", icon: Calculator, blurb: "Run the numbers from this article on our instant EMI calculator." },
};

function suggestedCalculator(post) {
  const text = `${(post.categories || []).join(" ")} ${(post.tags || []).map((t) => t.name).join(" ")} ${post.title}`.toLowerCase();
  if (/balance transfer|switch/.test(text)) return CALCULATORS.savings;
  if (/eligib|how much|income/.test(text)) return CALCULATORS.eligibility;
  return CALCULATORS.emi;
}

const ReadingProgress = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 140, damping: 28, mass: 0.4 });
  return (
    <motion.div
      data-testid="reading-progress-bar"
      className="fixed inset-x-0 top-0 z-[60] h-[3px] origin-left bg-brass"
      style={{ scaleX }}
      aria-hidden="true"
    />
  );
};

export default function BlogArticle() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState("loading");
  const allBlogs = useWP(fetchBlogPosts, FALLBACK_BLOGS, []);

  useEffect(() => {
    let live = true;
    setStatus("loading");
    fetchPostBySlug(slug)
      .then((p) => {
        if (!live) return;
        if (p) {
          setPost(p);
          setStatus("ready");
        } else {
          const fb = FALLBACK_BLOGS.find((b) => b.slug === slug);
          setPost(fb || null);
          setStatus(fb ? "ready" : "missing");
        }
      })
      .catch(() => {
        if (!live) return;
        const fb = FALLBACK_BLOGS.find((b) => b.slug === slug);
        setPost(fb || null);
        setStatus(fb ? "ready" : "missing");
      });
    return () => {
      live = false;
    };
  }, [slug]);

  const readingTime = useMemo(() => {
    if (!post) return 1;
    const words = stripHtml(post.content).split(/\s+/).length;
    return Math.max(1, Math.round(words / 200));
  }, [post]);

  const articleSchema = useMemo(() => {
    if (!post) return null;
    return {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BlogPosting",
          headline: post.title,
          description: post.excerpt || undefined,
          image: post.image || undefined,
          datePublished: post.date,
          author: { "@type": "Person", name: post.authorName || post.author || "Team Loan Salaah" },
          publisher: { "@type": "Organization", name: "Loan Salaah" },
          articleSection: post.categories?.[0] || post.category || "Guides",
          inLanguage: "en-IN",
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home" },
            { "@type": "ListItem", position: 2, name: "Blog" },
            { "@type": "ListItem", position: 3, name: post.title },
          ],
        },
      ],
    };
  }, [post]);

  useSEO({
    title: post ? `${post.title} | Loan Salaah Blog` : "Article | Loan Salaah",
    description: post?.excerpt || "",
    image: post?.image || undefined,
    type: "article",
    jsonLd: articleSchema,
  });

  if (status === "loading") {
    return (
      <div className="mx-auto max-w-3xl animate-pulse px-4 pb-24 pt-40" data-testid="blog-article-loading">
        <div className="h-4 w-24 rounded bg-sand" />
        <div className="mt-6 h-12 w-full rounded bg-sand" />
        <div className="mt-3 h-12 w-2/3 rounded bg-sand" />
        <div className="mt-10 aspect-[16/9] rounded-3xl bg-sand" />
      </div>
    );
  }

  if (status === "missing") {
    return (
      <div className="mx-auto max-w-3xl px-4 py-40 text-center" data-testid="blog-article-not-found">
        <h1 className="font-serif text-4xl font-semibold text-concordia">This article has moved or never existed.</h1>
        <Link to="/blog" className="mt-6 inline-block rounded-full bg-concordia px-7 py-3 text-sm font-semibold text-ivory">Browse the blog</Link>
      </div>
    );
  }

  const related = allBlogs.filter((b) => b.slug !== post.slug).slice(0, 2);
  const calc = suggestedCalculator(post);
  const CalcIcon = calc.icon;

  return (
    <article data-testid="blog-article" className="pb-24 pt-36">
      <ReadingProgress />
      <div className="mx-auto max-w-3xl px-4 sm:px-6">
        <Reveal>
          <Link to="/blog" data-testid="blog-back-link" className="group inline-flex items-center gap-2 text-sm font-medium text-slate transition-colors hover:text-concordia">
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
            All articles
          </Link>
          <p className="mt-8 font-mono text-[0.65rem] uppercase tracking-[0.3em] text-brass">
            {post.categories?.[0] || post.category || "Guides"}
          </p>
          <h1 className="mt-4 font-serif text-4xl font-semibold leading-tight tracking-tight text-concordia sm:text-5xl">
            {post.title}
          </h1>
          <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate" data-testid="blog-article-meta">
            <span className="flex items-center gap-2">
              {post.authorAvatar && (
                <img src={post.authorAvatar} alt="" className="h-6 w-6 rounded-full border border-sand" />
              )}
              <span className="font-semibold text-concordia" data-testid="blog-article-author">
                {post.authorName || post.author || "Team Loan Salaah"}
              </span>
            </span>
            <span className="h-1 w-1 rotate-45 bg-brass" aria-hidden="true" />
            <span>{formatDate(post.date)}</span>
            <span className="h-1 w-1 rotate-45 bg-brass" aria-hidden="true" />
            <span>{readingTime} min read</span>
            <span className="h-1 w-1 rotate-45 bg-brass" aria-hidden="true" />
            <span className="flex items-center gap-2" aria-label="Share this article">
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`${post.title} — ${window.location.href}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="article-share-whatsapp"
                aria-label="Share on WhatsApp"
                className="flex h-8 w-8 items-center justify-center rounded-full border border-sand text-slate transition-all duration-300 hover:border-concordia hover:bg-concordia hover:text-ivory"
              >
                <MessageCircle className="h-4 w-4" />
              </a>
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="article-share-linkedin"
                aria-label="Share on LinkedIn"
                className="flex h-8 w-8 items-center justify-center rounded-full border border-sand text-slate transition-all duration-300 hover:border-concordia hover:bg-concordia hover:text-ivory"
              >
                <Linkedin className="h-4 w-4" />
              </a>
            </span>
          </div>
        </Reveal>

        {post.image && (
          <Reveal delay={0.1} className="mt-10">
            <img src={post.image} alt={post.title} data-testid="blog-article-cover"
              className="aspect-[16/9] w-full rounded-3xl border border-sand object-cover" />
          </Reveal>
        )}

        <Reveal delay={0.15}>
          <div className="blog-content mt-10" data-testid="blog-article-content" dangerouslySetInnerHTML={{ __html: post.content }} />
        </Reveal>

        <Reveal>
          <Link
            to={calc.to}
            data-testid="blog-suggested-calculator"
            className="group mt-12 flex items-center justify-between gap-5 rounded-2xl border border-sand border-l-4 border-l-brass bg-white p-6 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_24px_50px_rgba(30,74,61,0.12)]"
          >
            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-concordia/[0.07] text-concordia transition-colors duration-500 group-hover:bg-concordia group-hover:text-ivory">
                <CalcIcon className="h-5 w-5" />
              </span>
              <div>
                <p className="font-mono text-[0.6rem] uppercase tracking-[0.28em] text-brass">Put this to work</p>
                <p className="mt-1 font-serif text-xl font-semibold text-concordia" data-testid="blog-suggested-calculator-name">{calc.label}</p>
                <p className="mt-1 text-sm leading-relaxed text-slate">{calc.blurb}</p>
              </div>
            </div>
            <ArrowRight className="h-5 w-5 shrink-0 text-brass transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>

        <div className="mt-10 rounded-3xl bg-concordia-ink p-8 text-ivory sm:p-10" data-testid="blog-article-cta">
          <h2 className="font-serif text-2xl font-semibold sm:text-3xl">Reading about loans? Planning one too?</h2>
          <p className="mt-3 max-w-lg text-sm leading-relaxed text-ivory/70">
            Run your numbers on our instant EMI calculator, or send an enquiry — an advisor compares partner offers for you.
          </p>
          <div className="mt-6 flex flex-wrap gap-4">
            <Link to="/emi-calculator" className="rounded-full border border-ivory/30 px-6 py-3 text-sm font-semibold transition-colors hover:border-brass hover:text-brass">
              EMI Calculator
            </Link>
            <Link to="/apply" className="group inline-flex items-center gap-2 rounded-full bg-ivory px-6 py-3 text-sm font-semibold text-concordia transition-all duration-300 hover:-translate-y-0.5">
              Apply Now
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        {(post.tags?.length > 0 || post.authorName || post.author) && (
          <div className="mt-10">
            {post.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="blog-article-tags">
                {post.tags.map((t) => (
                  <Link
                    key={t.slug}
                    to={`/blog?tag=${t.slug}`}
                    data-testid={`blog-tag-${t.slug}`}
                    className="rounded-full border border-sand bg-white px-4 py-1.5 text-xs font-semibold text-charcoal/70 transition-all duration-300 hover:border-concordia hover:text-concordia"
                  >
                    #{t.name}
                  </Link>
                ))}
              </div>
            )}
            <div className="mt-6 flex items-start gap-5 rounded-3xl border border-sand bg-white p-7" data-testid="blog-author-box">
              {post.authorAvatar ? (
                <img src={post.authorAvatar} alt={`${post.authorName || post.author} profile photo`}
                  className="h-16 w-16 shrink-0 rounded-full border-2 border-brass/40 object-cover" />
              ) : (
                <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-concordia font-serif text-2xl font-semibold text-ivory">
                  {(post.authorName || post.author || "T")[0]}
                </span>
              )}
              <div>
                <p className="font-mono text-[0.6rem] uppercase tracking-[0.28em] text-brass">Written by</p>
                <p className="mt-1 font-serif text-xl font-semibold text-concordia" data-testid="blog-author-name">
                  {post.authorName || post.author || "Team Loan Salaah"}
                </p>
                <p className="mt-2 text-sm leading-relaxed text-slate" data-testid="blog-author-bio">
                  {post.authorBio ||
                    "Guides from the Loan Salaah desk — practical, jargon-free loan knowledge for Indian borrowers, written from real DSA experience."}
                </p>
              </div>
            </div>
          </div>
        )}

        {related.length > 0 && (
          <div className="mt-14">
            <h2 className="font-serif text-2xl font-semibold text-concordia">Keep reading</h2>
            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              {related.map((b) => (
                <Link key={b.slug} to={`/blog/${b.slug}`} data-testid={`related-article-${b.slug}`}
                  className="group rounded-3xl border border-sand bg-white p-6 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_24px_50px_rgba(30,74,61,0.12)]">
                  <p className="font-mono text-[0.62rem] uppercase tracking-[0.25em] text-brass">{b.categories?.[0] || b.category || "Guides"}</p>
                  <h3 className="mt-2 font-serif text-lg font-semibold leading-snug text-concordia">{b.title}</h3>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </article>
  );
}
